import { useEffect, useState, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Download, FileText, Loader2, RotateCcw, Inbox, Layers, Gauge, Wallet, ReceiptText, Cpu, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const COLUMNS = [
  { key: "pdv", label: "PdV", cls: "font-medium", render: (r) => r.pdv },
  { key: "client_num", label: "# Cliente", render: (r) => r.client_num },
  { key: "client", label: "Cliente", render: (r) => r.client || "—" },
  { key: "service_num", label: "# Servicio", cls: "text-muted-foreground", render: (r) => r.service_num },
  { key: "datetime", label: "Fecha / Hora", nowrap: true, render: (r) => fmtDate(r.datetime) },
  { key: "volume", label: "Volumen", num: true, render: (r) => fmtNum(r.volume) },
  { key: "price", label: "Precio", num: true, render: (r) => fmtMoney(r.price) },
  { key: "dscto", label: "Dscto", num: true, render: (r) => fmtMoney(r.dscto) },
  { key: "amount", label: "Importe", num: true, cls: "font-medium", render: (r) => fmtMoney(r.amount) },
  { key: "lon", label: "Longitud", num: true, render: (r) => fmtCoord(r.lon) },
  { key: "lat", label: "Latitud", num: true, render: (r) => fmtCoord(r.lat) },
  { key: "alarma", label: "Alarma", render: (r) => (r.alarma ?? "") },
  { key: "formapago", label: "Pago", render: (r) => r.formapago || "—" },
  { key: "recibido", label: "Recibido", nowrap: true, render: (r) => fmtDate(r.datetime) },
  { key: "totalizer", label: "Totalizador", num: true, cls: "text-muted-foreground", render: (r) => fmtNum(r.totalizer) },
];

const fmtMoney = (v) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);
const fmtNum = (v) => new Intl.NumberFormat("es-MX", { minimumFractionDigits: 2 }).format(v || 0);
const fmtInt = (v) => new Intl.NumberFormat("es-MX").format(v || 0);
const fmtCoord = (v) => (v === null || v === undefined || v === "" ? "—" : Number(v).toFixed(6));
const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));

const getDefaultRange = () => {
  const pad = (n) => String(n).padStart(2, "0");
  const toYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const today = toYMD(new Date());
  return { date_from: today, date_to: today };
};

const PAGE_SIZE = 25;
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" });
};

export default function Dashboard() {
  const { user } = useAuth();
  const isSuper = user.role === "superadmin";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(isSuper ? "" : user.company_id || "");
  const [pdvs, setPdvs] = useState([]);
  const [filters, setFilters] = useState({ ...getDefaultRange(), pdv: "", client_code: user.client_code || "" });
  const [appliedFilters, setAppliedFilters] = useState({ ...getDefaultRange(), pdv: "", client_code: user.client_code || "" });
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [byPdv, setByPdv] = useState([]);
  const [byDay, setByDay] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const reqIdRef = useRef(0);

  const cidQs = isSuper && companyId ? `?company_id=${companyId}` : "";

  useEffect(() => {
    if (isSuper) api.get("/companies-options").then((r) => setCompanies(r.data)).catch(() => {});
  }, [isSuper]);

  useEffect(() => {
    if (isSuper && !companyId) {
      setRows([]);
      setTotals(null);
      setSearched(false);
      setPdvs([]);
      return;
    }
    api.get(`/servicios/pdvs${cidQs}`).then((r) => setPdvs(r.data)).catch(() => {});
    runQuery({ ...getDefaultRange(), pdv: "", client_code: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const runQuery = async (f, p = 1) => {
    const rid = ++reqIdRef.current;
    setLoading(true);
    try {
      const payload = {
        date_from: f.date_from || null,
        date_to: f.date_to || null,
        pdv: f.pdv || null,
        client_code: f.client_code || null,
        page: p,
        page_size: PAGE_SIZE,
      };
      const { data } = await api.post(`/servicios/query${cidQs}`, payload);
      if (rid !== reqIdRef.current) return;
      setRows(data.rows);
      setTotals(data.totals);
      setTotal(data.total ?? data.totals?.count ?? data.rows.length);
      setByPdv(data.by_pdv || []);
      setByDay(data.by_day || []);
      setAppliedFilters(f);
      setPage(data.page || p);
      setSearched(true);
      if (data.error) toast.error(data.error);
    } catch (e) {
      if (rid === reqIdRef.current) toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      if (rid === reqIdRef.current) setLoading(false);
    }
  };

  const handleSearch = () => runQuery(filters, 1);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const goToPage = (p) => {
    const target = Math.min(Math.max(1, p), totalPages);
    if (target !== page) runQuery(appliedFilters, target);
  };
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);
  const pageWindow = (() => {
    const win = [];
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    const end = Math.min(totalPages, Math.max(page + 2, 5));
    for (let i = start; i <= end; i++) win.push(i);
    return win;
  })();

  const rangeLabel = () => {
    const d = (x) => (x ? new Date(x + "T00:00:00").toLocaleDateString("es-MX") : "—");
    if (!appliedFilters.date_from && !appliedFilters.date_to) return "Todos";
    return `${d(appliedFilters.date_from)} a ${d(appliedFilters.date_to)}`;
  };
  const handleReset = () => {
    const cleared = { ...getDefaultRange(), pdv: "", client_code: user.client_code || "" };
    setFilters(cleared);
    runQuery(cleared, 1);
  };

  const fmtCompact = (v) =>
    new Intl.NumberFormat("es-MX", { notation: "compact", maximumFractionDigits: 1 }).format(v || 0);

  const fetchAllRows = async () => {
    const f = appliedFilters;
    const { data } = await api.post(`/servicios/query${cidQs}`, {
      date_from: f.date_from || null,
      date_to: f.date_to || null,
      pdv: f.pdv || null,
      client_code: f.client_code || null,
      page: 1,
      page_size: 0,
    });
    return data.rows || [];
  };

  const KPIS = totals
    ? [
        { label: "Registros", value: fmtNum(totals.count).replace(/\.00$/, ""), icon: Layers },
        { label: "Volumen total", value: fmtNum(totals.volume), icon: Gauge },
        { label: "Importe total", value: fmtMoney(totals.amount), icon: ReceiptText },
        { label: "Descuento total", value: fmtMoney(totals.dscto), icon: Wallet },
      ]
    : [];

  const exportExcel = async () => {
    if (!total) return toast.error("No hay datos para exportar");
    const allRows = await fetchAllRows();
    if (!allRows.length) return toast.error("No hay datos para exportar");
    const MONEY = '"$"#,##0.00', QTY = "#,##0.00", COORD = "0.000000";
    const cols = [
      { h: "PdV", a: (r) => r.pdv },
      { h: "# Cliente", a: (r) => r.client_num },
      { h: "Cliente", a: (r) => r.client },
      { h: "# Servicio", a: (r) => r.service_num },
      { h: "Fecha / Hora", a: (r) => fmtDate(r.datetime) },
      { h: "Volumen", a: (r) => num(r.volume), z: QTY, total: "volume" },
      { h: "Precio", a: (r) => num(r.price), z: MONEY },
      { h: "Dscto", a: (r) => num(r.dscto), z: MONEY, total: "dscto" },
      { h: "Importe", a: (r) => num(r.amount), z: MONEY, total: "amount" },
      { h: "Longitud", a: (r) => num(r.lon), z: COORD },
      { h: "Latitud", a: (r) => num(r.lat), z: COORD },
      { h: "Alarma", a: (r) => r.alarma },
      { h: "Pago", a: (r) => r.formapago },
      { h: "Recibido", a: (r) => fmtDate(r.datetime) },
      { h: "Totalizador", a: (r) => num(r.totalizer), z: QTY },
    ];
    const N = cols.length;
    const HDR = 2; // fila de encabezados (0-indexed)
    const blank = (n) => Array(n).fill("");

    const aoa = [
      ["ZAE Clientes — Reporte de Servicios", ...blank(N - 1)],
      [`Generado: ${new Date().toLocaleString("es-MX")}   ·   Periodo: ${rangeLabel()}   ·   ${totals.count} registros`, ...blank(N - 1)],
      cols.map((c) => c.h),
    ];
    allRows.forEach((r) => aoa.push(cols.map((c) => { const v = c.a(r); return v === null || v === undefined ? "" : v; })));
    const totalRow = cols.map((c) =>
      c.total === "volume" ? totals.volume : c.total === "dscto" ? totals.dscto : c.total === "amount" ? totals.amount : ""
    );
    totalRow[0] = `TOTALES (${totals.count} registros)`;
    aoa.push(totalRow);
    const lastRow = aoa.length - 1;

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: N - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: N - 1 } },
    ];
    ws["!cols"] = cols.map((c, ci) => {
      let max = c.h.length;
      for (let ri = HDR + 1; ri < lastRow; ri++) {
        const v = aoa[ri][ci];
        const len = (v === null || v === undefined ? "" : String(v)).length;
        if (len > max) max = len;
      }
      return { wch: Math.min(Math.max(max + 2, 8), 34) };
    });
    ws["!rows"] = aoa.map((_, ri) => (ri === HDR ? { hpt: 22 } : ri < HDR ? { hpt: ri === 0 ? 22 : 16 } : {}));

    const thin = { style: "thin", color: { rgb: "E4E4E7" } };
    const border = { top: thin, bottom: thin, left: thin, right: thin };
    const setStyle = (r, c, s) => {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (!ws[ref]) ws[ref] = { t: "s", v: "" };
      ws[ref].s = s;
    };

    setStyle(0, 0, { font: { bold: true, sz: 14, color: { rgb: "18181B" } }, alignment: { vertical: "center" } });
    setStyle(1, 0, { font: { italic: true, sz: 9, color: { rgb: "6B7280" } }, alignment: { vertical: "center" } });
    cols.forEach((c, ci) => {
      setStyle(HDR, ci, {
        font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "DC2626" } },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border,
      });
    });
    allRows.forEach((_, di) => {
      const r = HDR + 1 + di;
      const zebra = di % 2 === 1 ? { fill: { fgColor: { rgb: "FAFAFA" } } } : {};
      cols.forEach((c, ci) => {
        const ref = XLSX.utils.encode_cell({ r, c: ci });
        if (c.z && ws[ref] && typeof ws[ref].v === "number") ws[ref].z = c.z;
        setStyle(r, ci, {
          ...zebra,
          font: { sz: 10, color: { rgb: "27272A" } },
          alignment: { horizontal: c.z ? "right" : "left", vertical: "center" },
          border,
        });
      });
    });
    cols.forEach((c, ci) => {
      const ref = XLSX.utils.encode_cell({ r: lastRow, c: ci });
      if (c.total && ws[ref] && typeof ws[ref].v === "number") ws[ref].z = c.total === "volume" ? QTY : MONEY;
      setStyle(lastRow, ci, {
        font: { bold: true, sz: 10, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "DC2626" } },
        alignment: { horizontal: c.total ? "right" : "left", vertical: "center" },
        border,
      });
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Servicios");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), `servicios_${Date.now()}.xlsx`);
    toast.success("Excel exportado");
  };

  const exportPDF = async () => {
    if (!total) return toast.error("No hay datos para exportar");
    const allRows = await fetchAllRows();
    if (!allRows.length) return toast.error("No hay datos para exportar");
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const RED = [220, 38, 38];
    const DARK = [24, 24, 27];
    const GRAY = [107, 114, 128];

    // Encabezado
    doc.setFillColor(...RED);
    doc.rect(0, 0, pageW, 4, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(...DARK);
    doc.text("ZAE Clientes", 14, 16);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(...GRAY);
    doc.text("Reporte de Servicios", 14, 22);
    doc.setFontSize(9);
    const meta = `Generado: ${new Date().toLocaleString("es-MX")}   |   ${totals.count} registros`;
    doc.text(meta, pageW - 14, 16, { align: "right" });
    doc.text(`Periodo: ${rangeLabel()}`, pageW - 14, 22, { align: "right" });

    const numericIdx = new Set([5, 6, 7, 8, 9, 10, 14]); // Volumen, Precio, Dscto, Importe, Long, Lat, Totalizador
    const columnStyles = {};
    COLUMNS.forEach((c, i) => {
      columnStyles[i] = { halign: numericIdx.has(i) ? "right" : "left" };
    });

    autoTable(doc, {
      startY: 28,
      margin: { left: 10, right: 10 },
      head: [COLUMNS.map((c) => c.label)],
      body: allRows.map((r) => COLUMNS.map((c) => String(c.render(r)))),
      foot: [COLUMNS.map((c, idx) => {
        if (idx === 0) return `TOTALES (${totals.count})`;
        if (c.key === "volume") return fmtNum(totals.volume);
        if (c.key === "dscto") return fmtMoney(totals.dscto);
        if (c.key === "amount") return fmtMoney(totals.amount);
        return "";
      })],
      theme: "grid",
      styles: { fontSize: 6.2, cellPadding: 1.4, lineColor: [228, 228, 231], lineWidth: 0.1, textColor: [39, 39, 42], valign: "middle", overflow: "linebreak" },
      headStyles: { fillColor: RED, textColor: 255, fontStyle: "bold", fontSize: 6.5, halign: "center", valign: "middle" },
      footStyles: { fillColor: RED, textColor: 255, fontStyle: "bold", fontSize: 6.5, halign: "right" },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      columnStyles,
      didDrawPage: (data) => {
        const page = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(...GRAY);
        doc.text(`Página ${data.pageNumber} de ${page}`, pageW - 14, doc.internal.pageSize.getHeight() - 6, { align: "right" });
        doc.text("ZAE Clientes", 14, doc.internal.pageSize.getHeight() - 6);
      },
    });
    doc.save(`servicios_${Date.now()}.pdf`);
    toast.success("PDF exportado");
  };

  return (
    <div className="w-full max-w-[1800px] mx-auto p-4 sm:p-6 lg:p-8">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Consulta de Servicios</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Filtra y exporta la información de tus servicios administrados.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm gap-2" onClick={exportExcel} data-testid="export-excel-button">
            <Download className="h-4 w-4" /> Excel
          </Button>
          <Button variant="outline" className="rounded-sm gap-2" onClick={exportPDF} data-testid="export-pdf-button">
            <FileText className="h-4 w-4" /> PDF
          </Button>
        </div>
      </div>

      {((user.equipment_numbers && user.equipment_numbers.length > 0) || user.client_code) && (
        <div className="mb-6 flex items-center gap-2 text-sm bg-[#DC2626]/5 border border-[#DC2626]/20 text-[#DC2626] rounded-sm px-4 py-2.5" data-testid="equipment-scope-banner">
          <Cpu className="h-4 w-4 shrink-0" />
          <span>
            {user.equipment_numbers && user.equipment_numbers.length > 0 && `Mostrando únicamente las ventas de tus ${user.equipment_numbers.length} equipo(s) asignados.`}
            {user.client_code && ` Filtrado fijo por cliente #${user.client_code}.`}
          </span>
        </div>
      )}

      {isSuper && (
        <div className="bg-card border border-border rounded-sm p-5 mb-6 max-w-sm">
          <Label className="label-caps">Empresa</Label>
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger className="rounded-sm mt-2" data-testid="consulta-company-select"><SelectValue placeholder="Seleccionar empresa" /></SelectTrigger>
            <SelectContent>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {isSuper && !companyId ? (
        <div className="bg-card border border-dashed border-border rounded-sm py-16 text-center text-muted-foreground" data-testid="consulta-empty">
          Selecciona una empresa para consultar sus servicios.
        </div>
      ) : (
      <>
      <div className="bg-card border border-border rounded-sm p-6 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          <div className="space-y-2">
            <Label className="label-caps">Fecha desde</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="rounded-sm" data-testid="filter-date-from" />
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Fecha hasta</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="rounded-sm" data-testid="filter-date-to" />
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Punto de Venta</Label>
            <Select value={filters.pdv || "all"} onValueChange={(v) => setFilters({ ...filters, pdv: v === "all" ? "" : v })}>
              <SelectTrigger className="rounded-sm" data-testid="filter-pdv"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {pdvs.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Código de Cliente</Label>
            <Input value={filters.client_code} onChange={(e) => setFilters({ ...filters, client_code: e.target.value })} placeholder="C-1001" disabled={!!user.client_code} className="rounded-sm disabled:opacity-70 disabled:bg-muted" data-testid="filter-client-code" />
            {user.client_code && <p className="text-xs text-[#DC2626]">Filtro fijo asignado a tu usuario.</p>}
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <Button className="rounded-sm gap-2 bg-[#DC2626] hover:bg-[#DC2626]/90" onClick={handleSearch} disabled={loading} data-testid="search-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Consultar
          </Button>
          <Button variant="outline" className="rounded-sm gap-2" onClick={handleReset} data-testid="reset-button">
            <RotateCcw className="h-4 w-4" /> Limpiar
          </Button>
        </div>
      </div>

      {totals && rows.length > 0 && (
        <div className="mb-6" data-testid="kpi-panel">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {KPIS.map((k) => (
              <div key={k.label} className="bg-card border border-border rounded-sm p-5" data-testid={`kpi-${k.label}`}>
                <div className="flex items-center justify-between">
                  <span className="label-caps">{k.label}</span>
                  <k.icon className="h-4 w-4 text-[#DC2626]" />
                </div>
                <div className="font-heading text-2xl font-bold tracking-tight mt-3 tabular-nums">{k.value}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-sm p-5">
              <h3 className="label-caps mb-4">Importe por Punto de Venta</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={byPdv} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                  <XAxis dataKey="pdv" tick={{ fontSize: 11, fill: "#52525B" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#52525B" }} axisLine={false} tickLine={false} tickFormatter={fmtCompact} />
                  <Tooltip formatter={(v) => fmtMoney(v)} cursor={{ fill: "#F4F4F5" }} contentStyle={{ borderRadius: 2, border: "1px solid #E4E4E7", fontSize: 12 }} />
                  <Bar dataKey="importe" name="Importe" fill="#DC2626" radius={[2, 2, 0, 0]} maxBarSize={64} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-card border border-border rounded-sm p-5">
              <h3 className="label-caps mb-4">Tendencia de Importe</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={byDay} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="impGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#DC2626" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#DC2626" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                  <XAxis dataKey="d" tick={{ fontSize: 10, fill: "#52525B" }} axisLine={false} tickLine={false} tickFormatter={(d) => d.slice(5)} minTickGap={24} />
                  <YAxis tick={{ fontSize: 11, fill: "#52525B" }} axisLine={false} tickLine={false} tickFormatter={fmtCompact} />
                  <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ borderRadius: 2, border: "1px solid #E4E4E7", fontSize: 12 }} />
                  <Area type="monotone" dataKey="importe" name="Importe" stroke="#DC2626" strokeWidth={2} fill="url(#impGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="results-table">
            <thead>
              <tr className="bg-muted border-b border-border">
                {COLUMNS.map((c) => (
                  <th key={c.key} className={`px-4 py-3 label-caps whitespace-nowrap ${c.num ? "text-right" : "text-left"}`} data-testid={`th-${c.key}`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.pdv}-${r.service_num}-${r.datetime}`} className="border-b border-border" data-testid={`row-${i}`}>
                  {COLUMNS.map((c) => (
                    <td
                      key={c.key}
                      className={`px-4 py-2.5 ${c.num ? "text-right tabular-nums" : ""} ${c.nowrap ? "whitespace-nowrap" : ""} ${c.cls || ""}`}
                    >
                      {c.render(r)}
                    </td>
                  ))}
                </tr>
              ))}
              {searched && !rows.length && (
                <tr>
                  <td colSpan={COLUMNS.length} className="py-16 text-center text-muted-foreground">
                    <Inbox className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    No se encontraron registros con los filtros seleccionados.
                  </td>
                </tr>
              )}
            </tbody>
            {totals && rows.length > 0 && (
              <tfoot>
                <tr className="bg-[#DC2626] text-white font-semibold" data-testid="totals-row">
                  <td className="px-4 py-3 whitespace-nowrap" colSpan={5}>TOTALES ({totals.count} registros)</td>
                  {COLUMNS.slice(5).map((c) => {
                    let content = "";
                    if (c.key === "volume") content = fmtNum(totals.volume);
                    else if (c.key === "dscto") content = fmtMoney(totals.dscto);
                    else if (c.key === "amount") content = fmtMoney(totals.amount);
                    return (
                      <td key={c.key} className={`px-4 py-3 ${c.num ? "text-right tabular-nums" : ""}`} data-testid={c.key === "amount" ? "total-amount" : undefined}>
                        {content}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {total > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-3 mt-4" data-testid="pagination">
          <span className="text-sm text-muted-foreground" data-testid="pagination-info">
            Mostrando <span className="font-medium text-foreground">{fmtInt(rangeStart)}–{fmtInt(rangeEnd)}</span> de <span className="font-medium text-foreground">{fmtInt(total)}</span> registros
          </span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="rounded-sm px-2" disabled={page <= 1 || loading} onClick={() => goToPage(1)} data-testid="page-first"><ChevronsLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" className="rounded-sm px-2" disabled={page <= 1 || loading} onClick={() => goToPage(page - 1)} data-testid="page-prev"><ChevronLeft className="h-4 w-4" /></Button>
            {pageWindow.map((p) => (
              <Button
                key={p}
                variant="outline"
                size="sm"
                className={`rounded-sm min-w-9 ${p === page ? "bg-[#DC2626] hover:bg-[#DC2626]/90 text-white border-[#DC2626]" : ""}`}
                onClick={() => goToPage(p)}
                disabled={loading}
                data-testid={`page-${p}`}
              >
                {p}
              </Button>
            ))}
            <Button variant="outline" size="sm" className="rounded-sm px-2" disabled={page >= totalPages || loading} onClick={() => goToPage(page + 1)} data-testid="page-next"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" className="rounded-sm px-2" disabled={page >= totalPages || loading} onClick={() => goToPage(totalPages)} data-testid="page-last"><ChevronsRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
